import { useHandler } from "@redotech/react-util/hook";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import {
  rcoDeliveryDistanceMilesSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  DeliveryDistanceMilesSegment,
  SegmentableCheckoutCharacteristic,
  SegmentNumberOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

export const DeliveryDistanceMilesInspectorContent = memo(
  function DeliveryDistanceMilesInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const segment = useMemo(
      () => rcoDeliveryDistanceMilesSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Condition
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          Delivery distance is within {segment.miles} miles
        </Text>
        <UsOnlyWarningBadge />
      </Flex>
    );
  },
);

export const EditableDeliveryDistanceMilesInspectorContent = memo(
  function EditableDeliveryDistanceMilesInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoDeliveryDistanceMilesSegmentSchema.parse(step.segment),
      [step],
    );

    const handleMilesChange = useHandler((miles: number) => {
      const updatedSegment: DeliveryDistanceMilesSegment = {
        segmentableCharacteristic:
          SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES,
        miles,
        operator: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            Delivery distance is within
          </Text>
        </Flex>
        <RedoIncrementDecrement
          min={1}
          setValue={handleMilesChange}
          size="small"
          step={1}
          suffix="miles"
          value={segment.miles}
        />
        <UsOnlyWarningBadge />
      </>
    );
  },
);

const UsOnlyWarningBadge = memo(function UsOnlyWarningBadge() {
  return (
    <RedoBadge
      color="warning"
      segmentLeading={{ type: "icon", Icon: AlertCircleIcon }}
      size="sm"
      text="US domestic shipments only"
    />
  );
});
